/*
 * component-collection-toolbar.js — Slice 19 (Collection page) Phase A.
 *
 * Two behaviours, both scoped per main-collection section instance:
 *
 *   1. Grid-density switch (DESKTOP ONLY). Clicking a density button sets the
 *      desktop column count on the results grid (the `--cols` custom property +
 *      `data-cols` attribute) and persists it to localStorage so it survives
 *      navigation. It NEVER touches the mobile column count (`--cols-mobile`
 *      stays as the merchant-configured cprod_cols_mobile). No reload, no
 *      server param. The active button is resolved here from the grid's real
 *      column count (the section cannot read the block's resolved cols — S20).
 *
 *   2. Sort. Changing the sort <select> navigates to the sorted URL, preserving
 *      all existing query params and setting sort_by. A <noscript> submit button
 *      is the no-JS fallback.
 *
 *      Phase B2: when filtering is enabled, the facet engine
 *      (component-collection-facets.js) owns sort-change via an SRA no-reload
 *      commit — exactly ONE sort path. This handler therefore bails when the
 *      filter panel ([data-facets]) is present (the render-order-independent
 *      signal that the engine is loaded). When filtering is OFF the engine is
 *      not loaded, so this full-navigation handler remains the sole sort path.
 *
 * Bootstraps on DOMContentLoaded + shopify:section:load (theme-editor reload).
 * IIFE — leaks nothing to global scope.
 */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'kv-collection-cols';

  // Per-collection storage key so a shopper's choice on one collection does
  // not bleed across all collections. Falls back to the flat key when no
  // collection handle is available.
  function storageKey(handle) {
    return handle ? STORAGE_PREFIX + ':' + handle : STORAGE_PREFIX;
  }

  function isEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function getStoredCols(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function setStoredCols(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* private mode / quota — ignore */
    }
  }

  function applyCols(grid, cols) {
    grid.style.setProperty('--cols', cols);
    grid.setAttribute('data-cols', cols);
  }

  function syncActive(buttons, cols) {
    buttons.forEach(function (btn) {
      var isActive = btn.getAttribute('data-cols') === String(cols);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function initDensity(section) {
    var density = section.querySelector('.section-collection__density');
    if (!density) return;

    var buttons = Array.prototype.slice.call(
      density.querySelectorAll('.section-collection__density-btn')
    );
    if (!buttons.length) return;

    var results = section.querySelector('[data-collection-results]');
    function grid() { return section.querySelector('.collection-products__grid'); }

    var initialGrid = grid();
    if (!initialGrid) return;

    var key = storageKey(initialGrid.getAttribute('data-collection-handle'));

    var desired = initialGrid.getAttribute('data-cols');
    if (!isEditor()) {
      var stored = getStoredCols(key);
      if (stored && buttons.some(function (b) { return b.getAttribute('data-cols') === stored; })) {
        desired = stored;
      }
    }

    function apply() {
      var g = grid();
      if (!g) return;
      applyCols(g, desired);
      syncActive(buttons, desired);
    }

    apply();

    density.addEventListener('click', function (event) {
      var btn = event.target.closest('.section-collection__density-btn');
      if (!btn || !density.contains(btn)) return;
      var cols = btn.getAttribute('data-cols');
      if (!cols) return;
      desired = cols;
      apply();
      if (!isEditor()) {
        setStoredCols(key, cols);
      }
    });

    if (results && 'MutationObserver' in window) {
      new MutationObserver(function () { apply(); }).observe(results, { childList: true });
    }
  }

  function initSort(section) {
    var select = section.querySelector('[data-collection-sort]');
    if (!select) return;

    // Phase B2: the SRA facet engine owns sort-change when filtering is enabled.
    // [data-facets] (the filter panel) renders iff filtering is on AND iff the
    // facet engine script is loaded — a reliable, render-order-independent proxy.
    // Bail so there is exactly one sort path (no double commit / full-nav race).
    if (section.querySelector('[data-facets]')) return;

    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      // A new sort resets pagination to page 1.
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  }

  // Top-bar pill strip scroll arrows (Slice 19 C3 G3b). Ported generic from
  // section-faq.js setupTabArrows; wired to [data-topbar-wrap] ONLY. No-op when
  // absent (every layout but top_bar). scrollLeft/scrollBy are JS — C2 governs
  // CSS only, so logical-axis scrolling here is fine.
  function setupTopbarArrows(section) {
    var wrap = section.querySelector('[data-topbar-wrap]');
    if (!wrap) return;
    var track = wrap.querySelector('[data-topbar-track]');
    var prev = wrap.querySelector('.section-collection__topbar-arrow--prev');
    var next = wrap.querySelector('.section-collection__topbar-arrow--next');
    if (!track || !prev || !next) return;

    function update() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 1) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = track.scrollLeft <= 1;
      next.hidden = track.scrollLeft >= maxScroll - 1;
    }

    function scrollByAmount(dir) {
      var amount = Math.max(track.clientWidth * 0.8, 100);
      track.scrollBy({ left: dir * amount, behavior: 'smooth' });
    }

    prev.addEventListener('click', function () { scrollByAmount(-1); });
    next.addEventListener('click', function () { scrollByAmount(1); });
    track.addEventListener('scroll', function () {
      window.requestAnimationFrame(update);
    });
    window.addEventListener('resize', function () {
      window.requestAnimationFrame(update);
    });
    update();
  }

  function initSection(section) {
    if (!section || section.dataset.collectionToolbarInit === 'true') return;
    section.dataset.collectionToolbarInit = 'true';
    initDensity(section);
    initSort(section);
    setupTopbarArrows(section);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sections = scope.querySelectorAll('.section-collection');
    Array.prototype.forEach.call(sections, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
